// React, Next
import { Fragment, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Image, { StaticImageData } from 'next/image'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'

// Img
import IcPeopleChat from 'public/img/icons/people-chat.svg'
import IcChecklistSearch from 'public/img/icons/checklist-search.svg'
import IcReportCharts from 'public/img/icons/report-charts.svg'
import IcGearCheck from 'public/img/icons/gear-check.svg'

// Custom
import Section from '@/components/sections/Section'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody, getFontBodySmall, getFontH2 } from '@/assets/theme/fonts'

/**
 * Tints of the brand blue (themeColors.bg.start) - the palette has no light
 * blue of its own. The green accents are kept for the eyebrow, rule and step
 * connectors, matching the rest of the page.
 */
const BLUE_BG = 'rgba(90, 109, 205, 0.06)'
const BLUE_SOFT = 'rgba(90, 109, 205, 0.14)'
const BLUE_BANNER = 'rgba(90, 109, 205, 0.11)'

/** The step icons are square (128x128), so a single size covers both axes. */
const ICON_SIZE = 48

type Step = {
  number: string
  icon: StaticImageData
  title: string
  description: string
}

export default function ProcessSection() {
  const { t } = useTranslation('servicesOtCybersecurity')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  // Below this the row of steps stops fitting, so it stacks vertically.
  const stacked = w < breakpoints.desktopToMobile

  const steps: Step[] = [
    {
      number: '01',
      icon: IcPeopleChat,
      title: t('process.steps.1.title'),
      description: t('process.steps.1.description')
    },
    {
      number: '02',
      icon: IcChecklistSearch,
      title: t('process.steps.2.title'),
      description: t('process.steps.2.description')
    },
    {
      number: '03',
      icon: IcReportCharts,
      title: t('process.steps.3.title'),
      description: t('process.steps.3.description')
    },
    {
      number: '04',
      icon: IcGearCheck,
      title: t('process.steps.4.title'),
      description: t('process.steps.4.description')
    }
  ]

  const getNumberBadge = (number: string) => (
    <Box
      sx={{
        flexShrink: 0,
        width: '2.9rem',
        height: '2.9rem',
        borderRadius: '50%',
        background: BLUE_SOFT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: '1rem',
          fontWeight: 700,
          color: themeColors.bg.start
        }}
      >
        {number}
      </Typography>
    </Box>
  )

  /** Chevron joining one step to the next - horizontal on wide screens. */
  const getConnector = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: stacked ? 'center' : 'center',
        padding: stacked ? '0.35rem 0' : '0 0.35rem'
      }}
    >
      <Box
        sx={{
          width: '1.6rem',
          height: '1.6rem',
          borderRadius: '50%',
          background: themeColors.success.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {stacked ? (
          <KeyboardArrowDownIcon sx={{ fontSize: '1.1rem', color: '#fff' }} />
        ) : (
          <KeyboardArrowRightIcon sx={{ fontSize: '1.1rem', color: '#fff' }} />
        )}
      </Box>
    </Box>
  )

  const getStep = (step: Step) => (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: stacked ? 'row' : 'column',
        alignItems: stacked ? 'flex-start' : 'center',
        textAlign: stacked ? 'left' : 'center',
        gap: stacked ? '1rem' : 0,
        background: themeColors.kroemtech.background,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        padding: '1.5rem',
        boxSizing: 'border-box'
      }}
    >
      {getNumberBadge(step.number)}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: stacked ? 'flex-start' : 'center',
          width: '100%'
        }}
      >
        <Box sx={{ margin: stacked ? '0 0 0.5rem' : '1.25rem 0 1rem' }}>
          <Image src={step.icon} alt="" width={ICON_SIZE} height={ICON_SIZE} />
        </Box>

        <Typography
          variant="h3"
          component="h3"
          sx={{
            fontSize: '1rem',
            fontWeight: 700,
            lineHeight: 1.3,
            color: themeColors.kroemtech.dunkelblau
          }}
        >
          {step.title}
        </Typography>

        <Box
          sx={{
            width: stacked ? '100%' : '70%',
            borderTop: `1px solid ${themeColors.gray.lighter}`,
            margin: '0.9rem 0'
          }}
        />

        <Typography
          sx={{
            fontSize: getFontBodySmall(),
            lineHeight: 1.6,
            color: themeColors.gray.defaultText
          }}
        >
          {step.description}
        </Typography>
      </Box>
    </Box>
  )

  const getFooter = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: stacked ? 'flex-start' : 'center',
        gap: '1.25rem',
        background: BLUE_BANNER,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        padding: '1.5rem 1.75rem',
        marginTop: '2.5rem'
      }}
    >
      <ShieldOutlinedIcon
        sx={{ fontSize: '2.25rem', color: themeColors.bg.start }}
      />
      <Box>
        <Typography
          sx={{
            fontSize: getFontBody(),
            fontWeight: 700,
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '0.25rem'
          }}
        >
          {t('process.footer.title')}
        </Typography>
        <Typography
          sx={{
            fontSize: getFontBody(),
            lineHeight: 1.6,
            color: themeColors.gray.defaultText
          }}
        >
          {t('process.footer.text')}
        </Typography>
      </Box>
    </Box>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          fontSize: getFontBodySmall(),
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: themeColors.success.main,
          marginBottom: '0.5rem'
        }}
      >
        {t('process.eyebrow')}
      </Typography>
      <Box
        sx={{
          width: '2.5rem',
          borderTop: `3px solid ${themeColors.success.main}`,
          marginBottom: '1rem'
        }}
      />

      <Typography
        variant="h2"
        component="h2"
        fontSize={getFontH2()}
        sx={{
          fontWeight: 700,
          color: themeColors.kroemtech.dunkelblau,
          marginBottom: '1.25rem'
        }}
      >
        {t('process.title')}
      </Typography>

      <Typography
        sx={{
          fontSize: getFontBody(),
          lineHeight: 1.7,
          color: themeColors.gray.defaultText,
          maxWidth: '42rem',
          marginBottom: '2.5rem'
        }}
      >
        {t('process.description')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: stacked ? 'column' : 'row',
          alignItems: 'stretch'
        }}
      >
        {steps.map((step, i) => (
          <Fragment key={step.number}>
            {getStep(step)}
            {i < steps.length - 1 && getConnector()}
          </Fragment>
        ))}
      </Box>

      {getFooter()}
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={BLUE_BG}
      paddingTop="4.5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
